import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import { Link, useParams } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import NodeCrumbs from '../../shared/ProgramTree/NodeCrumbs';
import NodeTabs from '../../shared/ProgramTree/NodeTabs';
import useProgramPageTabs from '../../shared/ProgramTree/useProgramPageTabs';
import SPAButton from '../../shared/SPAButton';
import SPAPageHeader from '../../shared/SPAPageHeader';
import { useSPAToast } from '../../shared/SPAToast';
import { lessonGuidesPath, lessonSectionsPath, programLessonsPath, useContentMount } from '../contentMount';
import ProgramNavRail from './ProgramNavRail';
import { ProgramLesson, ProgramUnit } from './types';
import UnitCard from './UnitCard';
import useProgramLessons, { useReorderUnits } from './useProgramLessons';

const kbdClass = cn(
  'tw-rounded tw-border tw-border-solid tw-border-b-2 tw-border-spa-border-input tw-px-1',
  'tw-text-[0.6875rem] tw-text-spa-secondary',
);

const Kbd = ({ children }: { children: React.ReactNode }) => <kbd className={kbdClass}>{children}</kbd>;

const UnitlessLessonLink = ({ lesson, programId }: { lesson: ProgramLesson; programId: number }) => (
  <Link
    className="tw-text-spa-primary hover:tw-text-spa-accent-text-hover tw-no-underline"
    to={lessonSectionsPath(programId, lesson.id, useContentMount())}
  >
    {lesson.title}
  </Link>
);

const UnitlessTray = ({ lessons, programId }: { lessons: ProgramLesson[]; programId: number }) => (
  <section
    aria-labelledby="unitless-lessons"
    className="tw-mt-1.5 tw-rounded-lg tw-border tw-border-dashed tw-border-spa-border-input tw-px-4 tw-py-3"
  >
    <h3
      className={cn(
        'tw-m-0 tw-flex tw-flex-wrap tw-items-baseline tw-gap-2',
        'tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-spa-tertiary',
      )}
      id="unitless-lessons"
    >
      Unitless lessons
      <span className="tw-font-normal tw-normal-case tw-tracking-normal">
        · {lessons.length} {lessons.length === 1 ? 'lesson' : 'lessons'} not in any unit
        · add them from a unit’s Edit page
      </span>
    </h3>
    <ol className="tw-m-0 tw-mt-1.5 tw-grid tw-list-none tw-gap-x-6 tw-p-0 md:tw-grid-cols-2">
      {lessons.map(lesson => (
        <li key={lesson.id} className="tw-py-1 tw-text-sm">
          <UnitlessLessonLink lesson={lesson} programId={programId} />
        </li>
      ))}
    </ol>
  </section>
);

const EmptyUnits = ({ newUnitHref }: { newUnitHref: string }) => (
  <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-spa-border-input tw-px-5 tw-py-9 tw-text-center">
    <h3 className="tw-m-0 tw-mb-1.5 tw-text-base tw-font-bold tw-text-spa-heading">No units yet</h3>
    <p className="tw-mx-auto tw-mb-4 tw-mt-0 tw-max-w-[34ch] tw-text-sm tw-text-spa-secondary">
      Lessons show as one flat list on the table of contents until the program has units.
    </p>
    <SPAButton asChild variant="primary">
      <a href={newUnitHref}>New Unit</a>
    </SPAButton>
  </div>
);

const UnitsSkeleton = () => (
  <div aria-busy="true" className="tw-flex tw-flex-col tw-gap-3.5">
    <span className="tw-sr-only">Loading units</span>
    {[0, 1, 2].map(i => (
      <div key={i} className="tw-h-32 tw-animate-pulse tw-rounded-lg tw-bg-spa-bg-skeleton" />
    ))}
  </div>
);

// AIDEV-NOTE: SPA recreation of the legacy units page (/admin/programs/:id/units), routed on both
// mounts like the table of contents and sharing its data hook: one card per unit with the facts
// the legacy page hid behind Edit, drag-to-reorder saved on drop, and Remove with a dialog that
// says what the legacy destroy actually does. Adding and editing units stay on the legacy forms.
const ProgramUnitsPage = () => {
  const { programId } = useParams<{ programId: string }>();
  const pid = Number(programId);
  const mount = useContentMount();
  const inSpa = mount === 'spa';
  const spaToast = useSPAToast();
  const {
    allLessons, ghostLessons, hasUnits, isLoading, isLoadingTitle, programColor, programTitle, units,
  } = useProgramLessons(pid);
  const reorderUnits = useReorderUnits(pid);
  // Committed synchronously on drop so the browser paints one state; cleared once the cache
  // catches up (see SPADataTable's localOrder for the same trick).
  const [localOrder, setLocalOrder] = useState<ProgramUnit[] | null>(null);

  useEffect(() => {
    setLocalOrder(null);
  }, [units]);

  const orderedUnits = localOrder ?? units;
  const tabPages = useProgramPageTabs(pid);
  const newUnitHref = Routes.new_admin_program_unit_path(pid);
  const publishedCount = units.filter(unit => unit.publish).length;

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const next = [...orderedUnits];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setLocalOrder(next);

    reorderUnits(next)
      .then(() => spaToast.success(`Saved unit order. ${moved.title} is now unit ${result.destination!.index + 1}.`))
      .catch(() => setLocalOrder(null));
  };

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      {inSpa && (
        <ProgramNavRail
          isLoadingTitle={isLoadingTitle}
          programColor={programColor}
          programId={pid}
          programTitle={programTitle}
        />
      )}
      <div className="tw-max-w-[1400px] tw-mx-auto tw-px-6 tw-py-4">
        <NodeCrumbs
          programId={pid}
          programTitle={programTitle}
          toLesson={id => lessonGuidesPath(pid, id, mount)}
          toProgram={id => programLessonsPath(id, mount)}
        />
        <SPAPageHeader
          actions={(
            <SPAButton asChild size="md" variant="primary">
              <a href={newUnitHref}>New Unit</a>
            </SPAButton>
          )}
          title="Units"
        />
        <div className="tw-mb-5 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border">
          <NodeTabs label="Program pages" pages={tabPages} />
        </div>

        {isLoading && <UnitsSkeleton />}

        {!isLoading && !hasUnits && <EmptyUnits newUnitHref={newUnitHref} />}

        {!isLoading && hasUnits && (
          <>
            <div
              className={cn(
                'tw-mb-3.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2',
                'tw-text-sm tw-text-spa-secondary',
              )}
            >
              <span>
                <b className="tw-font-bold tw-text-spa-heading">
                  {units.length} {units.length === 1 ? 'unit' : 'units'}
                </b>
                {' · '}{allLessons.length - ghostLessons.length} lessons · {publishedCount} published
              </span>
              <span className="tw-ml-auto tw-text-xs tw-text-spa-tertiary">
                Drag the handle to reorder, or focus it and press <Kbd>Space</Kbd> then <Kbd>↑</Kbd> <Kbd>↓</Kbd>
              </span>
            </div>

            {/* Card spacing is a margin on each item, not a flex gap: react-beautiful-dnd lays
                siblings out by their margins while one is lifted, so a gap would collapse. */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="program-units">
                {droppableProvided => (
                  <ol
                    ref={droppableProvided.innerRef}
                    className="tw-m-0 tw-list-none tw-p-0"
                    {...droppableProvided.droppableProps}
                  >
                    {orderedUnits.map((unit, index) => (
                      <Draggable key={unit.id} draggableId={String(unit.id)} index={index}>
                        {(dragProvided, snapshot) => (
                          <li ref={dragProvided.innerRef} className="tw-mb-3.5" {...dragProvided.draggableProps}>
                            <UnitCard
                              dragHandleProps={dragProvided.dragHandleProps}
                              isDragging={snapshot.isDragging}
                              programId={pid}
                              unit={unit}
                            />
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </ol>
                )}
              </Droppable>
            </DragDropContext>

            {ghostLessons.length > 0 && <UnitlessTray lessons={ghostLessons} programId={pid} />}
          </>
        )}
      </div>
    </div>
  );
};

export default ProgramUnitsPage;
